import {
  Button,
  Card,
  Divider,
  Field,
  Input,
  makeStyles,
  shorthands,
  Spinner,
  Switch,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  Add24Regular,
  ArrowLeftRegular,
  DismissRegular,
  Mail24Regular,
  SaveRegular,
  Send24Regular,
} from "@fluentui/react-icons";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiPermissionBanner } from "../components/ApiPermissionBanner";
import {
  MessageBar,
  MessageBarBody,
} from "../components/Common/ScrollableMessageBar";
import { triggerAdminConsent } from "../hooks/useAdminConsent";
import useEventTracker from "../hooks/useEventTracker";
import { useGeneralConfig } from "../hooks/useGeneralConfig";
import { useScrollableMessage } from "../hooks/useScrollableMessage";
import { apiClient } from "../services/apiClient";
import { ElementButton, PageName } from "../tracking/tracking.types";
import { useCommonSettingsStyles } from "./commonSettingsStyles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("24px"),
    maxWidth: "1200px",
    ...shorthands.margin("0", "auto"),
    ...shorthands.padding("24px"),
  },
  card: {
    ...shorthands.padding("24px"),
    ...shorthands.gap("20px"),
    display: "flex",
    flexDirection: "column",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    ...shorthands.margin("0", "0", "16px", "0"),
    color: tokens.colorBrandForeground1,
  },
  formGrid: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("20px"),
  },
  actionButtons: {
    display: "flex",
    ...shorthands.gap("12px"),
    ...shorthands.margin("20px", "0", "0", "0"),
  },
  recipientList: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap("8px"),
    ...shorthands.margin("8px", "0", "0", "0"),
  },
  recipientBadge: {
    padding: "4px 8px",
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
  },
  switchGroup: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("12px"),
  },
});

interface UnifiedEmailSettings {
  // General settings
  enabled: boolean;
  fromAddress: string;
  defaultRecipients: string[];

  // System notifications
  driftNotificationEnabled: boolean;
  policyUpdateNotificationEnabled: boolean;
  systemAlertEnabled: boolean;

  // Domain feed notifications
  domainFeedNotificationsEnabled: boolean;
  domainFeedRecipients: string[];
  notifyOnApprovalRequired: boolean;
  notifyOnNewDomains: boolean;
  notifyOnRemovedDomains: boolean;
}

// Reusable component for email recipient badges to eliminate duplication
const RecipientBadge: React.FC<{
  email: string;
  enabled: boolean;
  onRemove: (email: string) => void;
  removeLabel: string;
}> = ({ email, enabled, onRemove, removeLabel }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 8px",
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
      borderRadius: tokens.borderRadiusMedium,
      fontSize: "14px",
    }}
    data-testid="email-settings-recipient-badge-div"
  >
    <span data-testid="email-settings-recipient-email-span">{email}</span>
    {enabled && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          console.log("🖱️ Delete clicked for:", email);
          onRemove(email);
        }}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          padding: "2px",
          borderRadius: "50%",
          transition: "background-color 0.2s",
          border: "none",
          background: "transparent",
          color: "inherit",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
        title="Remove email"
        aria-label={removeLabel}
        data-testid="email-settings-remove-email-button"
      >
        <DismissRegular
          style={{ fontSize: "16px" }}
          data-testid="email-settings-dismiss-regular"
        />
      </button>
    )}
  </div>
);

export const EmailSettings: React.FC = () => {
  const styles = useStyles();
  const commonStyles = useCommonSettingsStyles();
  const navigate = useNavigate();
  const eventTracker = useEventTracker();
  const { config, loadingConfig, loadConfig } = useGeneralConfig();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useScrollableMessage<string | null>(null);
  const [success, setSuccess] = useScrollableMessage<string | null>(null);

  const savedSettingsRef = useRef<UnifiedEmailSettings | null>(null);

  // Unified email settings state
  const [settings, setSettings] = useState<UnifiedEmailSettings>({
    enabled: false,
    fromAddress: "", // Must be configured - no default
    defaultRecipients: [],
    driftNotificationEnabled: true,
    policyUpdateNotificationEnabled: true,
    systemAlertEnabled: true,
    domainFeedNotificationsEnabled: false,
    domainFeedRecipients: [],
    notifyOnApprovalRequired: true,
    notifyOnNewDomains: false,
    notifyOnRemovedDomains: false,
  });

  const [newRecipient, setNewRecipient] = useState("");
  const [newDomainFeedRecipient, setNewDomainFeedRecipient] = useState("");

  const hasChanges =
    savedSettingsRef.current !== null &&
    !isEqual(settings, savedSettingsRef.current);

  // Helper to validate email format using safe parsing approach
  const isValidEmail = (email: string): boolean => {
    if (!email || !email.trim()) return false;

    const trimmed = email.trim();

    // Basic structure validation without vulnerable regex patterns
    const atIndex = trimmed.indexOf("@");
    if (atIndex <= 0 || atIndex === trimmed.length - 1) return false;

    const localPart = trimmed.substring(0, atIndex);
    const domainPart = trimmed.substring(atIndex + 1);

    // Validate local part (before @) - length and dot placement
    if (
      localPart.length === 0 ||
      localPart.length > 64 ||
      localPart.startsWith(".") ||
      localPart.endsWith(".")
    )
      return false;
    if (localPart.includes("..")) return false;

    // Validate domain part (after @) - length, dot requirements, and dot placement
    if (
      domainPart.length === 0 ||
      domainPart.length > 253 ||
      !domainPart.includes(".") ||
      domainPart.startsWith(".") ||
      domainPart.endsWith(".")
    )
      return false;
    if (domainPart.includes("..")) return false;

    // Check for valid characters without regex quantifiers
    const validLocalChars = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]*$/;
    const validDomainChars = /^[a-zA-Z0-9.-]*$/;

    return validLocalChars.test(localPart) && validDomainChars.test(domainPart);
  };

  const hasMailPermissions = (config?.applications?.backend?.azure?.apiPermissions ?? [])
    .map(p => p.toLowerCase())
    .includes("mail.send");

  const permissionsMissing = !loadingConfig && config !== null && !hasMailPermissions;

  
  const loadEmailSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load general email settings
      const emailResponse = await apiClient.get<any>("/email/settings");
      // API returns { success: true, settings: {...} } wrapped in apiClient's data property 
      const emailData =
        (emailResponse as any)?.data?.settings ||
        (emailResponse as any)?.settings ||
        {};
      console.log("📧 Email API response:", emailData);

      // Load domain feed settings
      const domainFeedResponse = await apiClient.get<any>("/lseg/config");
      const domainFeedData = domainFeedResponse.data || domainFeedResponse;
      console.log("🔔 Domain feed API response:", domainFeedData);

      // Consolidate settings
      const newSettings = {
        // General email settings
        enabled: emailData?.enabled || false,
        fromAddress: emailData?.fromAddress || "", // No default - must be configured
        defaultRecipients: emailData?.defaultRecipients || [],
        driftNotificationEnabled: emailData?.driftNotificationEnabled ?? true,
        policyUpdateNotificationEnabled:
          emailData?.policyUpdateNotificationEnabled ?? true,
        systemAlertEnabled: emailData?.systemAlertEnabled ?? true,

        // Domain feed email settings
        domainFeedNotificationsEnabled:
          domainFeedData?.emailNotifications?.enabled || false,
        domainFeedRecipients:
          domainFeedData?.emailNotifications?.recipientEmails || [],
        notifyOnApprovalRequired:
          domainFeedData?.emailNotifications?.notifyOnApprovalRequired ?? true,
        notifyOnNewDomains:
          domainFeedData?.emailNotifications?.notifyOnNewDomains || false,
        notifyOnRemovedDomains:
          domainFeedData?.emailNotifications?.notifyOnRemovedDomains || false,
      };

      console.log("✅ Consolidated email settings to display:", newSettings);
      setSettings(newSettings);
      savedSettingsRef.current = newSettings;
    } catch (err: any) {
      console.error("❌ Failed to load email settings:", err);
      setError(
        `Failed to load email settings: ${err.message || "Unknown error"}`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmailSettings();
    loadConfig();
  }, [loadConfig, loadEmailSettings]);

  useEffect(() => {
    if (permissionsMissing && settings.enabled) {
      setSettings((prev) => ({ ...prev, enabled: false }));
    }
  }, [permissionsMissing, settings.enabled]);

  useEffect(() => {
    if (error) setSuccess(null);
  }, [error]);

  const handleAddRecipient = (type: "general" | "domainFeed") => {
    const email =
      type === "general" ? newRecipient.trim() : newDomainFeedRecipient.trim();
    setError(null);

    if (!isValidEmail(email)) {
      setError("Invalid email address. Please check the format and try again.");
      return;
    }

    if (type === "general") {
      if (!settings.defaultRecipients.includes(email)) {
        setSettings({
          ...settings,
          defaultRecipients: [...settings.defaultRecipients, email],
        });
        setNewRecipient("");
      } else
        setError("This email has already been added as a default recipient.");
    } else {
      if (!settings.domainFeedRecipients.includes(email)) {
        setSettings({
          ...settings,
          domainFeedRecipients: [...settings.domainFeedRecipients, email],
        });
        setNewDomainFeedRecipient("");
      } else
        setError("This email has already been added as a default recipient.");
    }
  };

  const handleRemoveRecipient = (
    email: string,
    type: "general" | "domainFeed",
  ) => {
    console.log("🗑️ Removing recipient:", email, "type:", type);
    console.log("   Current settings state:", settings);

    setError(null);

    if (type === "general") {
      const newRecipients = settings.defaultRecipients.filter(
        (r) => r !== email,
      );
      console.log("   Before:", settings.defaultRecipients);
      console.log("   After:", newRecipients);

      const newSettings = {
        ...settings,
        defaultRecipients: newRecipients,
      };
      console.log("   New settings object:", newSettings);
      setSettings(newSettings);
    } else {
      const newRecipients = settings.domainFeedRecipients.filter(
        (r) => r !== email,
      );
      console.log("   Before:", settings.domainFeedRecipients);
      console.log("   After:", newRecipients);

      const newSettings = {
        ...settings,
        domainFeedRecipients: newRecipients,
      };
      console.log("   New settings object:", newSettings);
      setSettings(newSettings);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate fromAddress is not empty and is a valid email
      if (!settings.fromAddress || !settings.fromAddress.trim()) {
        setError(
          "From Address is required. Please enter a valid email address.",
        );
        setSaving(false);
        return;
      }

      // Basic email validation using safe parsing approach (no vulnerable regex)
      if (!isValidEmail(settings.fromAddress)) {
        setError("From Address must be a valid email address.");
        setSaving(false);
        return;
      }

      eventTracker.eventTrackOnElementClicked({
        pageName: PageName.SETTINGS_EMAIL_NOTIFICATIONS,
        elementName: ElementButton.SAVE_SETTINGS,
        elementType: "button",
        emailNotificationEnabled: settings.enabled ? "ON" : "OFF",
        fromAddress: settings.fromAddress,
        defaultRecipients: settings.defaultRecipients,
        ...(settings.enabled && { emailForPolicyUpdates: settings.policyUpdateNotificationEnabled ? "ON" : "OFF" }),
        ...(settings.enabled && { emailForPolicyAlertsAndErrors: settings.systemAlertEnabled ? "ON" : "OFF" }),
      }).catch(() => {});

      // Save general email settings
      const emailSettings = {
        enabled: settings.enabled,
        fromAddress: settings.fromAddress,
        defaultRecipients: settings.defaultRecipients,
        driftNotificationEnabled: settings.driftNotificationEnabled,
        policyUpdateNotificationEnabled:
          settings.policyUpdateNotificationEnabled,
        systemAlertEnabled: settings.systemAlertEnabled,
      };

      console.log("📧 Saving general email settings:", emailSettings);
      await apiClient.post("/email/settings", emailSettings);

      // 🔧 FIX: Use dedicated email notifications endpoint instead of /lseg/config
      // This prevents overwriting the domain feed API key with undefined values
      const domainFeedEmailConfig = {
        emailNotifications: {
          enabled: settings.domainFeedNotificationsEnabled,
          recipientEmails: settings.domainFeedRecipients,
          recipientEmail: settings.domainFeedRecipients[0] || "",
          notifyOnApprovalRequired: settings.notifyOnApprovalRequired,
          notifyOnNewDomains: settings.notifyOnNewDomains,
          notifyOnRemovedDomains: settings.notifyOnRemovedDomains,
        },
      };

      console.log(
        "🔔 Saving domain feed email notifications:",
        domainFeedEmailConfig,
      );
      await apiClient.post("/lseg/email-notifications", domainFeedEmailConfig);

      setSuccess("✅ Email settings saved successfully!");

      await loadEmailSettings();
    } catch (err: any) {
      console.error("❌ Save error:", err);
      setError(`Failed to save settings: ${err.message || "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);

      eventTracker.eventTrackOnElementClicked({
        pageName: PageName.SETTINGS_EMAIL_NOTIFICATIONS,
        elementName: ElementButton.SEND_TEST_EMAIL,
        elementType: "button",
      }).catch(() => {});

      const testRecipient =
        settings.defaultRecipients[0] || settings.domainFeedRecipients[0];

      if (!testRecipient) {
        setError("Please add at least one recipient email address first");
        return;
      }

      await apiClient.post("/email/test", {
        to: testRecipient,
        from: settings.fromAddress,
      });

      setSuccess(`✅ Test email sent to ${testRecipient}! Check your inbox.`);
    } catch (err: any) {
      setError(`Test failed: ${err.message || "Unknown error"}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading || loadingConfig) {
    return (
      <div
        className={styles.container}
        data-testid="email-settings-container-div"
      >
        <div
          style={{ display: "flex", justifyContent: "center", padding: "48px" }}
          data-testid="email-settings-loading-center-div"
        >
          <Spinner
            size="large"
            label="Loading email settings..."
            data-testid="email-settings-loading-email-settings-spinner"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      data-testid="email-settings-container-2-div"
    >
      <div
        className={commonStyles.header}
        data-testid="email-settings-header-div"
      >
        <Text
          className={commonStyles.title}
          data-testid="email-settings-email-notification-settings-text"
        >
          <Mail24Regular data-testid="email-settings-mail24-regular" />
          Email Notification Settings
        </Text>
        <Button
          appearance="secondary"
          icon={
            <ArrowLeftRegular data-testid="email-settings-arrow-left-regular" />
          }
          onClick={() => navigate("/settings")}
          data-testid="email-settings-back-to-settings-button"
        >
          Back to Settings
        </Button>
      </div>

      {error && (
        <MessageBar
          intent="error"
          data-testid="email-settings-error-message-bar"
        >
          <MessageBarBody data-testid="email-settings-error-message-bar-body">
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {success && (
        <MessageBar
          intent="success"
          data-testid="email-settings-success-message-bar"
        >
          <MessageBarBody data-testid="email-settings-success-message-bar-body">
            {success}
          </MessageBarBody>
        </MessageBar>
      )}

      {permissionsMissing && (
        <ApiPermissionBanner
          message="Unable to send system emails."
          sessionStorageKey="email_settings_api_permission_banner_dismissed"
          testIdPrefix="email-settings"
          onGrantAccess={() => {
            if (config?.applications?.backend?.azure) {
              const { clientId, tenantId, displayName } = config.applications.backend.azure;
              triggerAdminConsent(clientId, tenantId, displayName);
            }
          }}
        />
      )}

      {/* General Email Configuration */}
      <Card className={styles.card} data-testid="email-settings-card-general">
        <Text
          className={styles.sectionTitle}
          data-testid="email-settings-general-email-configuration-text"
        >
          General Email Configuration
        </Text>

        <div
          className={styles.formGrid}
          data-testid="email-settings-form-grid-div"
        >
          <Field
            label="Enable Email Notifications"
            data-testid="email-settings-enable-email-notifications-field"
          >
            <Switch
              checked={settings.enabled}
              onChange={(_, data) =>
                setSettings({ ...settings, enabled: data.checked })
              }
              disabled={permissionsMissing}
              label={
                settings.enabled
                  ? "Email notifications enabled"
                  : "Email notifications disabled"
              }
              data-testid="email-settings-enable-notifications-switch"
            />
          </Field>

          <Field
            label="From Address"
            hint="Email address that notifications will be sent from"
            required
            validationState={
              settings.enabled && !isValidEmail(settings.fromAddress)
                ? "error"
                : "none"
            }
            validationMessage={
              settings.enabled && !isValidEmail(settings.fromAddress)
                ? "Please enter a valid email address"
                : ""
            }
            data-testid="email-settings-from-address-field"
          >
            <Input
              value={settings.fromAddress}
              onChange={(_, data) =>
                setSettings({ ...settings, fromAddress: data.value })
              }
              disabled={!settings.enabled || permissionsMissing}
              type="email"
              placeholder="your-email@example.com"
              data-testid="email-settings-your-email-example-com-input"
            />
          </Field>

          <Field
            label="Default Recipients"
            hint="Email addresses to receive general system notifications"
            data-testid="email-settings-default-recipients-field"
          >
            <div
              style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
              data-testid="email-settings-default-recipient-input-row-div"
            >
              <Input
                value={newRecipient}
                onChange={(_, data) => setNewRecipient(data.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && handleAddRecipient("general")
                }
                placeholder="Enter email address"
                type="email"
                disabled={!settings.enabled || permissionsMissing}
                style={{ flex: 1 }}
                data-testid="email-settings-general-enter-email-address-input"
              />

              <Button
                appearance="secondary"
                icon={
                  <Add24Regular data-testid="email-settings-general-add24-regular" />
                }
                onClick={() => handleAddRecipient("general")}
                disabled={!settings.enabled || permissionsMissing}
                data-testid="email-settings-general-add-button"
              >
                Add
              </Button>
            </div>

            <div
              className={styles.recipientList}
              data-testid="email-settings-recipient-list-div"
            >
              {settings.defaultRecipients.map((email, index) => (
                <RecipientBadge
                  key={email}
                  email={email}
                  enabled={settings.enabled && !permissionsMissing}
                  onRemove={(email) => handleRemoveRecipient(email, "general")}
                  removeLabel={`Remove email ${email}`}
                  data-testid={`email-settings-recipient-badge-${index}`}
                />
              ))}
              {settings.defaultRecipients.length === 0 && (
                <Text
                  size={200}
                  style={{ color: tokens.colorNeutralForeground3 }}
                  data-testid="email-settings-no-recipients-added-yet-text"
                >
                  No recipients added yet
                </Text>
              )}
            </div>
          </Field>
        </div>
      </Card>

      {/* System Notifications */}
      <Card
        className={styles.card}
        data-testid="email-settings-card-system-notifications"
      >
        <Text
          className={styles.sectionTitle}
          data-testid="email-settings-system-notifications-text"
        >
          System Notifications
        </Text>
        <Text
          size={300}
          style={{
            color: tokens.colorNeutralForeground2,
            marginBottom: "12px",
          }}
          data-testid="email-settings-configure-which-system-events-trigger-email-text"
        >
          Configure which system events trigger email notifications to default
          recipients
        </Text>

        <div
          className={styles.switchGroup}
          data-testid="email-settings-switch-group-div"
        >
          {/* <Field>
             <Switch
               checked={settings.driftNotificationEnabled}
               onChange={(_, data) => setSettings({ ...settings, driftNotificationEnabled: data.checked })}
               disabled={!settings.enabled}
               label="Policy drift detected"
             />
            </Field> */}

          <Field data-testid="email-settings-policy-updates-field">
            <Switch
              checked={settings.policyUpdateNotificationEnabled}
              onChange={(_, data) =>
                setSettings({
                  ...settings,
                  policyUpdateNotificationEnabled: data.checked,
                })
              }
              disabled={!settings.enabled || permissionsMissing}
              label="Policy updates and changes"
              data-testid="email-settings-policy-updates-and-changes-switch"
            />
          </Field>

          <Field data-testid="email-settings-system-alerts-field">
            <Switch
              checked={settings.systemAlertEnabled}
              onChange={(_, data) =>
                setSettings({ ...settings, systemAlertEnabled: data.checked })
              }
              disabled={!settings.enabled || permissionsMissing}
              label="System alerts and errors"
              data-testid="email-settings-system-alerts-and-errors-switch"
            />
          </Field>
        </div>
      </Card>

      {/* Domain Feed Notifications */}
      <Card
        className={styles.card}
        style={{ display: "none" }}
        data-testid="email-settings-card-domain-feed"
      >
        <Text
          className={styles.sectionTitle}
          data-testid="email-settings-domain-feed-notifications-text"
        >
          Domain Feed Notifications
        </Text>
        <Text
          size={300}
          style={{
            color: tokens.colorNeutralForeground2,
            marginBottom: "12px",
          }}
          data-testid="email-settings-configure-email-notifications-for-lseg-domain-text"
        >
          Configure email notifications for LSEG domain feed events
        </Text>

        <div
          className={styles.formGrid}
          data-testid="email-settings-form-grid-2-div"
        >
          <Field
            label="Enable Domain Feed Notifications"
            data-testid="email-settings-enable-domain-feed-notifications-field"
          >
            <Switch
              checked={settings.domainFeedNotificationsEnabled}
              onChange={(_, data) =>
                setSettings({
                  ...settings,
                  domainFeedNotificationsEnabled: data.checked,
                })
              }
              disabled={!settings.enabled || permissionsMissing}
              label={
                settings.domainFeedNotificationsEnabled
                  ? "Domain feed notifications enabled"
                  : "Domain feed notifications disabled"
              }
              data-testid="email-settings-domain-feed-notifications-switch"
            />
          </Field>

          {settings.domainFeedNotificationsEnabled && (
            <>
              <Field
                label="Domain Feed Recipients"
                hint="Email addresses to receive domain feed notifications"
                data-testid="email-settings-domain-feed-recipients-field"
              >
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                  data-testid="email-settings-domain-feed-recipient-input-row-div"
                >
                  <Input
                    value={newDomainFeedRecipient}
                    onChange={(_, data) =>
                      setNewDomainFeedRecipient(data.value)
                    }
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleAddRecipient("domainFeed")
                    }
                    placeholder="Enter email address"
                    type="email"
                    disabled={!settings.enabled || permissionsMissing}
                    style={{ flex: 1 }}
                    data-testid="email-settings-domain-feed-enter-email-address-input"
                  />

                  <Button
                    appearance="secondary"
                    icon={
                      <Add24Regular data-testid="email-settings-domain-feed-add24-regular" />
                    }
                    onClick={() => handleAddRecipient("domainFeed")}
                    disabled={!settings.enabled || permissionsMissing}
                    data-testid="email-settings-domain-feed-add-button"
                  >
                    Add
                  </Button>
                </div>

                <div
                  className={styles.recipientList}
                  data-testid="email-settings-recipient-list-2-div"
                >
                  {settings.domainFeedRecipients.map((email, index) => (
                    <RecipientBadge
                      key={email}
                      email={email}
                      enabled={settings.enabled && !permissionsMissing}
                      onRemove={(email) =>
                        handleRemoveRecipient(email, "domainFeed")
                      }
                      removeLabel={`Remove domain feed email ${email}`}
                      data-testid={`email-settings-recipient-badge-${index}`}
                    />
                  ))}
                  {settings.domainFeedRecipients.length === 0 && (
                    <Text
                      size={200}
                      style={{ color: tokens.colorNeutralForeground3 }}
                      data-testid="email-settings-no-domain-feed-recipients-added-yet-text"
                    >
                      No domain feed recipients added yet
                    </Text>
                  )}
                </div>
              </Field>

              <Divider data-testid="email-settings-divider" />

              <Text
                weight="semibold"
                style={{ marginBottom: "12px" }}
                data-testid="email-settings-notification-triggers-text"
              >
                Notification Triggers
              </Text>

              <div
                className={styles.switchGroup}
                data-testid="email-settings-switch-group-2-div"
              >
                <Field data-testid="email-settings-approval-required-field">
                  <Switch
                    checked={settings.notifyOnApprovalRequired}
                    onChange={(_, data) =>
                      setSettings({
                        ...settings,
                        notifyOnApprovalRequired: data.checked,
                      })
                    }
                    disabled={!settings.enabled || permissionsMissing}
                    label="Domain approval required (recommended)"
                    data-testid="email-settings-domain-approval-required-recommended-switch"
                  />
                </Field>

                <Field data-testid="email-settings-new-domains-field">
                  <Switch
                    checked={settings.notifyOnNewDomains}
                    onChange={(_, data) =>
                      setSettings({
                        ...settings,
                        notifyOnNewDomains: data.checked,
                      })
                    }
                    disabled={!settings.enabled || permissionsMissing}
                    label="New domains added to feed"
                    data-testid="email-settings-new-domains-added-to-feed-switch"
                  />
                </Field>

                <Field data-testid="email-settings-removed-domains-field">
                  <Switch
                    checked={settings.notifyOnRemovedDomains}
                    onChange={(_, data) =>
                      setSettings({
                        ...settings,
                        notifyOnRemovedDomains: data.checked,
                      })
                    }
                    disabled={!settings.enabled || permissionsMissing}
                    label="Domains removed from feed"
                    data-testid="email-settings-domains-removed-from-feed-switch"
                  />
                </Field>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Action Buttons */}
      <div
        className={styles.actionButtons}
        data-testid="email-settings-action-buttons-div"
      >
        <Button
          appearance="primary"
          icon={<SaveRegular data-testid="email-settings-save-regular" />}
          onClick={handleSave}
          disabled={
            saving ||
            permissionsMissing ||
            !hasChanges ||
            (settings.enabled && !isValidEmail(settings.fromAddress))
          }
          data-testid="email-settings-save-settings-button"
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>

        <Button
          appearance="secondary"
          icon={<Send24Regular data-testid="email-settings-send24-regular" />}
          onClick={handleTestEmail}
          disabled={
            testing ||
            permissionsMissing ||
            !settings.enabled ||
            !isValidEmail(settings.fromAddress) ||
            (settings.defaultRecipients.length === 0 &&
              settings.domainFeedRecipients.length === 0)
          }
          data-testid="email-settings-send-test-email-button"
        >
          {testing ? "Sending..." : "Send Test Email"}
        </Button>
      </div>
    </div>
  );
};
